// sim/timescale.ts — LAYERED TIMESCALE ARCHITECTURE: the cadence ladder that keeps
// the major sim systems from "competing" on one clock.
//
// THE PROBLEM it solves: the big systems used to run on cadences clustered in a
// single ~5–24 tick band (leaderboard 5, capacity 16, AI 24, projects 20). So
// evolution, growth, combat and civilization all turned over on the SAME timescale,
// and none of them read as its own layer of the simulation. This module spreads them
// across FOUR layers with order-of-magnitude gaps, so each layer moves at its own
// pace: moment-to-moment behaviour every tick, growth over "minutes", evolution over
// "tens of minutes", civilization over "hours".
//
// THE FOUR LAYERS (fastest → slowest), each one step = `period[layer]` ticks:
//   LAYER 0 Moment        — movement, pathing, gathering, combat. Runs EVERY tick.
//   LAYER 1 Growth        — population growth, hive expansion, logistics.
//   LAYER 2 Evolution     — gene acquisition, generation turnover, trait propagation.
//   LAYER 3 Civilization  — region domination, megaprojects, major specialization.
//
// THE SPEC INVARIANTS (the orderings this architecture must honour):
//   • population (Growth) turns over FASTER than Evolution   → period[Growth] < period[Evolution]
//   • Evolution is SLOWER than combat (Moment)               → period[Evolution] > period[Moment]
//   • Civilization is SLOWER than Evolution                  → period[Civilization] > period[Evolution]
//   A strictly-increasing period ladder satisfies all three; validateTimescale enforces it.
//
// DETERMINISM — THE REFERENCE-TPS RULE (read this before touching the ladder):
//   The ladder is computed from a FIXED reference tps (DEFAULT_TIMESCALE_TPS), baked
//   at build time, NEVER from the live playback `ticksPerSecond` in web settings.
//   Playback tps is a WALL-CLOCK knob (how fast ticks are pumped); the SIM must reach
//   identical state at tick N regardless of how fast it was played, or save replay
//   breaks. So: raising playback speed changes pace only, never sim-time behaviour.
//   Everything here is pure integer math, allocation-free, and folds into the state
//   hash via fnvU32 — same seed + ticks ⇒ identical state. The whole layer is OPT-IN
//   (a world without a TimescaleConfig is byte-identical to the pre-timescale world).

import { fnvU32 } from '../hash.ts';

/**
 * The four timescale layers, fastest → slowest. Used as indices into
 * TimescaleConfig.period (and as the values of SYSTEM_LAYER). `as const` — NOT an
 * enum (Node 22 type-stripping forbids enums in `.ts` run directly).
 */
export const LAYER = {
  /** Moment (seconds): movement, pathing, gathering, combat. Every tick. */
  Moment: 0,
  /** Growth (minutes): population growth, hive expansion, logistics. */
  Growth: 1,
  /** Evolution (tens of minutes): gene acquisition, generation turnover. */
  Evolution: 2,
  /** Civilization (hours): region domination, megaprojects, specialization. */
  Civilization: 3,
} as const;
export type Layer = (typeof LAYER)[keyof typeof LAYER];

/** Human name per LAYER.* (index = layer), for census / debug output. */
export const LAYER_NAME = ['Moment', 'Growth', 'Evolution', 'Civilization'] as const;

/**
 * The FIXED sim reference tps the ladder is derived from. Matches the web/save.ts
 * default (`ticksPerSecond: 6`) so the shipped pacing reads naturally, but this is a
 * BUILD-TIME constant: the ladder must NEVER be computed from live playback tps (see
 * the reference-tps rule in the header). Bumping playback speed changes wall-clock
 * pace only; sim-time behaviour at a given tick is unchanged.
 */
export const DEFAULT_TIMESCALE_TPS = 6;

/**
 * The timescale ladder: ticks per one step of each layer, indexed by LAYER.*.
 * `period` is length 4 and strictly increasing (validateTimescale enforces it).
 */
export interface TimescaleConfig {
  /** The reference tps the ladder was derived from (baked, not live playback tps). */
  readonly tps: number;
  /** Ticks per one step of each layer, indexed by LAYER.* (length 4, increasing). */
  readonly period: readonly number[];
}

/**
 * The shipped ladder, derived from a FIXED reference tps (defaulting to the sim
 * reference DEFAULT_TIMESCALE_TPS — callers must NOT pass live playback tps here).
 *
 * growth  = max(2, round(tps · 4))   — one growth step ≈ 4 reference-seconds
 * period  = [1, growth, growth·8, growth·8·4]
 *
 * At tps = 6 → [1, 24, 192, 768]: Moment every tick, Growth every 24 ticks (≈4s),
 * Evolution every 192 ticks (≈32s), Civilization every 768 ticks (≈128s). Each layer
 * is an order of magnitude slower than the one above it. Validated before return.
 */
export function defaultTimescaleConfig(tps = DEFAULT_TIMESCALE_TPS): TimescaleConfig {
  const growth = Math.max(2, Math.round(tps * 4));
  const cfg: TimescaleConfig = {
    tps,
    period: [1, growth, growth * 8, growth * 8 * 4],
  };
  validateTimescale(cfg);
  return cfg;
}

/**
 * Assert a ladder is well-formed: exactly 4 periods, Moment ≥ 1, STRICTLY INCREASING
 * across all four, and each of the three spec invariants. Throws a clear Error on any
 * violation (these are programmer errors — a bad ladder must fail loud at build time,
 * never silently mis-pace the sim). Pure; no allocation beyond the message string.
 */
export function validateTimescale(cfg: TimescaleConfig): void {
  const p = cfg.period;
  if (p.length !== 4) {
    throw new Error(`timescale: period must have length 4 (got ${p.length})`);
  }
  if ((p[LAYER.Moment] as number) < 1) {
    throw new Error(`timescale: period[Moment] must be >= 1 (got ${p[LAYER.Moment]})`);
  }
  // Strictly increasing across the whole ladder (this alone implies the three spec
  // invariants, but we check those explicitly below for clear, targeted errors).
  for (let i = 1; i < 4; i++) {
    if ((p[i] as number) <= (p[i - 1] as number)) {
      throw new Error(
        `timescale: period must be strictly increasing; ` +
          `period[${i}]=${p[i]} <= period[${i - 1}]=${p[i - 1]}`,
      );
    }
  }
  // Spec invariant 1: population (Growth) faster than Evolution.
  if ((p[LAYER.Growth] as number) >= (p[LAYER.Evolution] as number)) {
    throw new Error(
      `timescale: Growth must be faster than Evolution ` +
        `(period[Growth]=${p[LAYER.Growth]} < period[Evolution]=${p[LAYER.Evolution]})`,
    );
  }
  // Spec invariant 2: Evolution slower than combat (Moment).
  if ((p[LAYER.Evolution] as number) <= (p[LAYER.Moment] as number)) {
    throw new Error(
      `timescale: Evolution must be slower than Moment ` +
        `(period[Evolution]=${p[LAYER.Evolution]} > period[Moment]=${p[LAYER.Moment]})`,
    );
  }
  // Spec invariant 3: Civilization slower than Evolution.
  if ((p[LAYER.Civilization] as number) <= (p[LAYER.Evolution] as number)) {
    throw new Error(
      `timescale: Civilization must be slower than Evolution ` +
        `(period[Civilization]=${p[LAYER.Civilization]} > period[Evolution]=${p[LAYER.Evolution]})`,
    );
  }
}

/** Ticks per one step of `layer` (its cadence). */
export function layerPeriod(cfg: TimescaleConfig, layer: Layer): number {
  return cfg.period[layer] as number;
}

/**
 * Whether `layer` is due on `tick`. Moment is ALWAYS due (every tick); every other
 * layer is due exactly on multiples of its period. Pure integer test — this is the
 * cadence gate a system calls in `update`.
 */
export function layerDue(cfg: TimescaleConfig, layer: Layer, tick: number): boolean {
  return layer === LAYER.Moment ? true : tick % (cfg.period[layer] as number) === 0;
}

/**
 * Ticks spanning `seconds` of reference time at the ladder's reference tps (≥ 1).
 * A doc/test convenience for reasoning about the ladder in human units — NOT for the
 * hot path. Uses cfg.tps (the baked reference), never live playback tps.
 */
export function ticksForSeconds(cfg: TimescaleConfig, seconds: number): number {
  return Math.max(1, Math.round(cfg.tps * seconds));
}

/**
 * The ARCHITECTURE MAP: which layer each major system lives on. This is the
 * documentation-as-data of the design — when you add or re-home a system, record its
 * layer here. Every value is a valid LAYER.* (0..3). `as const` so the keys/values
 * are literal-typed and the test can assert the whole mapping.
 *
 *   Moment       — runs every tick: organism behaviour, wildlife, pathing, the task
 *                  scheduler, combat.
 *   Growth       — population/economy turnover: colony spawn governance, carrying
 *                  capacity, the AI decision cadence, task posting, hive upkeep.
 *   Evolution    — genetic/standings turnover: gene acquisition, the leaderboard,
 *                  discovery, the evolution box.
 *   Civilization — the slowest, world-shaping layer: region-scale projects, rivals.
 */
export const SYSTEM_LAYER = {
  // --- Moment (every tick) ---
  organisms: LAYER.Moment,
  wildlife: LAYER.Moment,
  pathing: LAYER.Moment,
  scheduler: LAYER.Moment,
  combat: LAYER.Moment,
  // --- Growth ---
  colony: LAYER.Growth,
  capacity: LAYER.Growth,
  ai: LAYER.Growth,
  tasks: LAYER.Growth,
  hive: LAYER.Growth,
  // --- Evolution ---
  genes: LAYER.Evolution,
  leaderboard: LAYER.Evolution,
  discovery: LAYER.Evolution,
  evolutionBox: LAYER.Evolution,
  // --- Civilization ---
  projects: LAYER.Civilization,
  rivals: LAYER.Civilization,
} as const;

/**
 * Fold a timescale config into a running FNV hash (the reference tps + every period,
 * in fixed order). Called ONLY from a guarded path so a timescale-off world stays
 * byte-identical. Mirrors the fnvU32-folding idiom used across the sim hashes.
 */
export function hashTimescale(h: number, cfg: TimescaleConfig): number {
  let acc = fnvU32(h, cfg.tps);
  for (let i = 0; i < cfg.period.length; i++) {
    acc = fnvU32(acc, cfg.period[i] as number);
  }
  return acc >>> 0;
}
